import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { judgeFits } from '../../../shared/lessonFill.mjs';
import { loadPose, loadTemplate, previewHtml, SLIDE_GAP } from '../utils/lessonPreview';
import type { LessonPackage, LessonPlatform, LessonPost } from '../utils/lessonPreview';
import { storeZip } from '../utils/storeZip';
import { copyText } from '../utils/clipboard';
import './SocialPanelPage.css';

/**
 * Social panel, phase 1 (2026-10-29): lesson posts. Odin imports a finished
 * post file, checks every slide for Instagram and TikTok side by side in a live
 * preview (the real template, filled the way the Mac fills it, with its own fit
 * script), approves each platform's version, and downloads the final images the
 * Mac made with render-lesson-posts.mjs --engine. Facebook uses the Instagram
 * images. Approve stays off while any slide is over its limit (65% Instagram,
 * 80% TikTok). Routes: /api/social/panel/ (workers/src/routes/socialPanel.js).
 */

type Status = 'draft' | 'approved' | 'final';

interface Version {
  id: string;
  platform: string;
  status: Status;
  template_sha: string | null;
  fit: string[] | null;
  approved_at: string | null;
  render_fit: string[] | null;
  images: { slide: number; key: string; bytes: number | null }[];
  rendered_at: string | null;
  note: string | null;
}

interface Post {
  id: string;
  name: string;
  type: string;
  source: string;
  caption: string;
  fields: Record<string, string>;
  pose_picks?: Record<string, string>; // pose slots the Engine picked on import (pose rotation, Odin 2026-10-31)
  package: LessonPackage | null;
  versions: Version[];
}

interface PostRow {
  id: string;
  name: string;
  type: string;
  versions: { id: string; platform: string; status: Status; note: string | null }[];
}

const PLATFORM_LABEL: Record<string, string> = { instagram: 'Instagram', tiktok: 'TikTok' };
const STATUS_LABEL: Record<Status, string> = { draft: 'Draft', approved: 'Approved, waiting for the Mac', final: 'Final images in' };
const PREVIEW_WIDTH = 240;
const MAC_COMMAND = 'cd ~/Projects/"Words Am What Master"/05-SOCIAL/scripts && node render-lesson-posts.mjs --engine';

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ---------- one platform's live preview ----------

function PlatformPreview({ post, pkg, platform, onFits }: {
  post: Post; pkg: LessonPackage; platform: LessonPlatform; onFits: (fits: string[] | null, error?: string) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fits, setFits] = useState<string[] | null>(null);
  const token = useMemo(() => crypto.randomUUID(), [post.id, platform.sha256]);

  useEffect(() => {
    let cancelled = false;
    setHtml(null); setFits(null); setError(null); onFits(null);
    (async () => {
      try {
        const template = await loadTemplate(platform.sha256);
        const poses: Record<string, string> = {};
        for (const slot of pkg.pose_slots) poses[slot] = await loadPose(post.fields[slot]);
        const values: LessonPost = { ...post.fields, format: post.type, caption: post.caption };
        if (!cancelled) setHtml(previewHtml(template, platform, values, poses, pkg, token));
      } catch (e) {
        if (!cancelled) { setError(errMsg(e)); onFits(null, errMsg(e)); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id, post.fields, platform.sha256, token]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      const d = e.data as { wawLessonFit?: string; fits?: unknown };
      if (d?.wawLessonFit !== token || !Array.isArray(d.fits)) return;
      const list = d.fits.map(String);
      setFits(list);
      onFits(list);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [token, onFits]);

  const scale = PREVIEW_WIDTH / platform.width;
  const pageHeight = pkg.slides * (platform.height + SLIDE_GAP);
  const judged = fits ? judgeFits(fits, platform.limit, pkg.slides) : null;

  if (error) return <div className="sp-error">Preview could not be drawn: {error}</div>;
  return (
    <div className="sp-preview">
      <div className="sp-frame-wrap" style={{ width: PREVIEW_WIDTH, height: pageHeight * scale }}>
        {html ? (
          <iframe
            ref={frame}
            title={`${PLATFORM_LABEL[platform.name] || platform.name} preview`}
            sandbox="allow-scripts"
            srcDoc={html}
            style={{ width: platform.width, height: pageHeight, transform: `scale(${scale})` }}
          />
        ) : <div className="sp-loading">Drawing the slides…</div>}
      </div>
      <ol className="sp-fits">
        {Array.from({ length: pkg.slides }, (_, i) => {
          const s = judged?.slides[i];
          return (
            <li key={i} style={{ height: (platform.height + SLIDE_GAP) * scale }} className={s?.bad ? 'bad' : ''}>
              <span className="sp-fit-n">Slide {i + 1}</span>
              <span className="sp-fit-text">{s ? s.text || 'not measured' : 'measuring…'}</span>
              {s?.fill != null && <span className="sp-fit-pct">{s.fill}% of {platform.limit}%</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ---------- final images ----------

function FinalImages({ post, version }: { post: Post; version: Version }) {
  const [urls, setUrls] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const made: string[] = [];
    (async () => {
      try {
        for (const img of version.images) {
          const { bytes } = await api.getBytes(`/api/social/panel/versions/${version.id}/slides/${img.slide}`);
          made.push(URL.createObjectURL(new Blob([bytes], { type: 'image/png' })));
        }
        if (!cancelled) setUrls([...made]);
      } catch (e) {
        if (!cancelled) setError(errMsg(e));
      }
    })();
    return () => { cancelled = true; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [version.id, version.images]);

  const download = async () => {
    setBusy(true); setError(null);
    try {
      const entries = [];
      for (const img of version.images) {
        const { bytes } = await api.getBytes(`/api/social/panel/versions/${version.id}/slides/${img.slide}`);
        entries.push({ name: `slide-${String(img.slide).padStart(2, '0')}.png`, data: new Uint8Array(bytes) });
      }
      entries.push({ name: 'caption.txt', data: new TextEncoder().encode(post.caption.trim() + '\n') });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(storeZip(entries));
      a.download = `${post.name}-${version.platform}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sp-final">
      <button className="sp-btn primary" onClick={download} disabled={busy}>
        {busy ? 'Preparing…' : `Download ${PLATFORM_LABEL[version.platform] || version.platform} images`}
      </button>
      {version.platform === 'instagram' && <div className="sp-hint">Facebook uses these Instagram images.</div>}
      {error && <div className="sp-error">{error}</div>}
      <div className="sp-thumbs">
        {urls.map((u, i) => <a key={u} href={u} download={`slide-${String(i + 1).padStart(2, '0')}.png`}><img src={u} alt={`Slide ${i + 1}`} /></a>)}
      </div>
    </div>
  );
}

// ---------- one platform column ----------

function PlatformColumn({ post, pkg, platform, version, mode, onChanged, onFitsChange }: {
  post: Post; pkg: LessonPackage; platform: LessonPlatform; version: Version; mode: 'platform' | 'post';
  onChanged: (p: Post) => void; onFitsChange: (platform: string, fits: string[] | null) => void;
}) {
  const [fits, setFits] = useState<string[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFits = useCallback((f: string[] | null, err?: string) => {
    setFits(f);
    setPreviewError(err || null);
    onFitsChange(platform.name, f);
  }, [platform.name, onFitsChange]);

  const judged = fits ? judgeFits(fits, platform.limit, pkg.slides) : null;
  const label = PLATFORM_LABEL[platform.name] || platform.name;

  let blocked: string | null = null;
  if (version.status !== 'draft') blocked = null;
  else if (mode === 'post') blocked = 'Approval is per post: use Approve post above.';
  else if (previewError) blocked = 'The preview could not be drawn.';
  else if (!judged) blocked = 'Measuring the slides…';
  else if (!judged.ok) blocked = `Approve is off: ${judged.problems.join('; ')}.`;

  const approve = async () => {
    setBusy(true); setError(null);
    try {
      onChanged(await api.post(`/api/social/panel/versions/${version.id}/approve`, { fits, template_sha: platform.sha256 }) as Post);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const unapprove = async () => {
    setBusy(true); setError(null);
    try {
      onChanged(await api.post(`/api/social/panel/versions/${version.id}/unapprove`, {}) as Post);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="sp-col">
      <header className="sp-col-head">
        <div>
          <h3>{label}</h3>
          <div className="sp-col-meta">{platform.width}×{platform.height}, limit {platform.limit}%</div>
        </div>
        <span className={`sp-chip ${version.status}`}>{STATUS_LABEL[version.status]}</span>
      </header>

      {version.note && <div className="sp-note">{version.note}</div>}

      {version.status === 'draft' && (
        <div className="sp-actions">
          <button className="sp-btn primary" onClick={approve} disabled={busy || !!blocked}>Approve {label}</button>
          {blocked && <div className="sp-hint">{blocked}</div>}
        </div>
      )}
      {version.status === 'approved' && (
        <div className="sp-actions">
          <div className="sp-hint">Approved. The Mac makes the final images: <code>{MAC_COMMAND}</code></div>
          <button className="sp-btn" onClick={unapprove} disabled={busy}>Unapprove</button>
        </div>
      )}
      {version.status === 'final' && <FinalImages post={post} version={version} />}
      {error && <div className="sp-error">{error}</div>}

      {version.render_fit && (
        <table className="sp-compare">
          <thead><tr><th>Slide</th><th>Approved on (panel)</th><th>Mac render</th></tr></thead>
          <tbody>
            {version.render_fit.map((m, i) => {
              const p = version.fit?.[i] ?? '';
              return <tr key={i} className={version.fit && p !== m ? 'differs' : ''}><td>{i + 1}</td><td>{p || '—'}</td><td>{m}</td></tr>;
            })}
          </tbody>
        </table>
      )}

      {version.status !== 'final' && <PlatformPreview post={post} pkg={pkg} platform={platform} onFits={handleFits} />}
    </section>
  );
}

// ---------- page ----------

export default function SocialPanelPage() {
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [post, setPost] = useState<Post | null>(null);
  const [mode, setMode] = useState<'platform' | 'post'>('platform');
  const [error, setError] = useState<string | null>(null);
  const [importLog, setImportLog] = useState<{ name: string; ok: boolean; text: string }[]>([]);
  const [importing, setImporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [postFits, setPostFits] = useState<Record<string, string[] | null>>({});
  const [postBusy, setPostBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const loadList = useCallback(async () => {
    try {
      const d = await api.get('/api/social/panel/posts') as { posts: PostRow[] };
      setPosts(d.posts);
      setError(null);
    } catch (e) { setError(errMsg(e)); }
  }, []);

  useEffect(() => {
    loadList();
    api.get('/api/social/panel/settings').then((s) => setMode((s as { approval: 'platform' | 'post' }).approval)).catch(() => {});
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) { setPost(null); return; }
    let cancelled = false;
    setPostFits({});
    api.get(`/api/social/panel/posts/${selectedId}`)
      .then((p) => { if (!cancelled) setPost(p as Post); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const changed = useCallback((p: Post) => { setPost(p); loadList(); }, [loadList]);
  const onFitsChange = useCallback((platform: string, fits: string[] | null) => {
    setPostFits((cur) => ({ ...cur, [platform]: fits }));
  }, []);

  const importFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setImporting(true);
    const log: { name: string; ok: boolean; text: string }[] = [];
    let lastId: string | null = null;
    for (const f of Array.from(files)) {
      try {
        let parsed: unknown;
        try { parsed = JSON.parse(await f.text()); } catch (e) { throw new Error(`cannot read it as JSON: ${errMsg(e)}`); }
        const r = await api.post('/api/social/panel/posts/import', { name: f.name, post: parsed }) as { replaced: boolean; post: Post };
        log.push({ name: f.name, ok: true, text: r.replaced ? 'replaced the earlier import; both versions are back to draft' : 'imported' });
        lastId = r.post.id;
      } catch (e) {
        log.push({ name: f.name, ok: false, text: errMsg(e) });
      }
    }
    setImportLog(log);
    setImporting(false);
    if (fileInput.current) fileInput.current.value = '';
    await loadList();
    if (lastId) { setSelectedId(null); setTimeout(() => setSelectedId(lastId), 0); }
  };

  const setApprovalMode = async (next: 'platform' | 'post') => {
    try {
      const s = await api.put('/api/social/panel/settings', { approval: next }) as { approval: 'platform' | 'post' };
      setMode(s.approval);
    } catch (e) { setError(errMsg(e)); }
  };

  const pkg = post?.package || null;
  const versionOf = (name: string) => post?.versions.find((v) => v.platform === name);
  const words = post ? Object.keys(post.fields).filter((k) => /^item\d+\.word$/.test(k)).map((k) => post.fields[k]).join(', ') : '';

  const postApproveBlocked = (() => {
    if (!pkg || !post) return 'No post.';
    if (!pkg.platforms.every((p) => versionOf(p.name)?.status === 'draft')) return 'Both versions must be drafts.';
    for (const p of pkg.platforms) {
      const f = postFits[p.name];
      if (!f) return 'Measuring the slides…';
      const j = judgeFits(f, p.limit, pkg.slides);
      if (!j.ok) return `Approve is off: ${PLATFORM_LABEL[p.name] || p.name} ${j.problems.join('; ')}.`;
    }
    return null;
  })();

  const approvePost = async () => {
    if (!post || !pkg) return;
    setPostBusy(true); setError(null);
    try {
      const versions = Object.fromEntries(pkg.platforms.map((p) => [p.name, { fits: postFits[p.name], template_sha: p.sha256 }]));
      changed(await api.post(`/api/social/panel/posts/${post.id}/approve`, { versions }) as Post);
    } catch (e) { setError(errMsg(e)); } finally { setPostBusy(false); }
  };

  return (
    <div className="sp-page">
      <header className="sp-header">
        <div>
          <h2 className="sp-title">Social</h2>
          <p className="sp-subtitle">
            Lesson posts. Import a finished post file, check every slide for Instagram and TikTok, approve each version,
            then download the final images the Mac makes. Approve stays off while a slide is over its limit
            (65% Instagram, 80% TikTok).
          </p>
        </div>
        <label className="sp-mode" title="Off: approve Instagram and TikTok separately. On: one Approve post for both.">
          <input type="checkbox" checked={mode === 'post'} onChange={(e) => setApprovalMode(e.target.checked ? 'post' : 'platform')} />
          Approve per post
        </label>
      </header>

      {error && <div className="sp-error">{error}</div>}

      <div className="sp-body">
        <aside className="sp-side">
          <div className="sp-import">
            <div className="sp-import-title">Import a post file</div>
            <input ref={fileInput} type="file" accept=".json,application/json" multiple disabled={importing}
              onChange={(e) => importFiles(e.target.files)} />
            <div className="sp-hint">From 05-SOCIAL/lesson-posts/. The file name becomes the post's name.</div>
            {importLog.map((l, i) => (
              <div key={i} className={`sp-import-line ${l.ok ? 'ok' : 'bad'}`}><b>{l.name}</b>: {l.text}</div>
            ))}
          </div>
          <ul className="sp-list">
            {posts.length === 0 && <li className="sp-empty">No posts yet.</li>}
            {posts.map((p) => (
              <li key={p.id}>
                <button className={`sp-list-item ${p.id === selectedId ? 'active' : ''}`} onClick={() => setSelectedId(p.id)}>
                  <span className="sp-list-name">{p.name}</span>
                  <span className="sp-list-meta">
                    {p.type}
                    {p.versions.map((v) => <span key={v.id} className={`sp-dot ${v.status}`} title={`${PLATFORM_LABEL[v.platform] || v.platform}: ${STATUS_LABEL[v.status]}`}>{(PLATFORM_LABEL[v.platform] || v.platform).slice(0, 2)}</span>)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="sp-main">
          {!post && <div className="sp-empty">Pick a post, or import one.</div>}
          {post && !pkg && <div className="sp-error">The {post.type} post type is not in the Engine. Run --sync on the Mac.</div>}
          {post && pkg && (
            <>
              <div className="sp-post-head">
                <div>
                  <h3 className="sp-post-name">{post.name}</h3>
                  <div className="sp-post-meta">{pkg.label} · {words}</div>
                  <div className="sp-post-meta">
                    {pkg.pose_slots.map((s, i) => (
                      <span key={s}>
                        {i > 0 && ' · '}{s} {post.fields[s]}
                        {post.pose_picks?.[s] && <span className="sp-pose-pick" title="Picked by the Engine on import from this format's pose set: never the last post's pose, then the one used longest ago."> (rotation pick)</span>}
                      </span>
                    ))}
                  </div>
                </div>
                {mode === 'post' && (
                  <div className="sp-actions">
                    <button className="sp-btn primary" onClick={approvePost} disabled={postBusy || !!postApproveBlocked}>Approve post</button>
                    {postApproveBlocked && <div className="sp-hint">{postApproveBlocked}</div>}
                  </div>
                )}
              </div>
              <div className="sp-caption">
                <div className="sp-caption-head">
                  <span>Caption</span>
                  <button className="sp-btn" onClick={async () => { if (await copyText(post.caption)) { setCopied(true); setTimeout(() => setCopied(false), 1500); } }}>
                    {copied ? 'Copied' : 'Copy caption'}
                  </button>
                </div>
                <p>{post.caption}</p>
              </div>
              <div className="sp-cols">
                {pkg.platforms.map((p) => {
                  const v = versionOf(p.name);
                  return v ? (
                    <PlatformColumn key={`${post.id}:${p.name}`} post={post} pkg={pkg} platform={p} version={v} mode={mode}
                      onChanged={changed} onFitsChange={onFitsChange} />
                  ) : null;
                })}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
